import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
  message,
} from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { useNavigate, useParams } from "react-router-dom";

const { Text } = Typography;
const { Option } = Select;

const EditProduct = () => {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("authUser"))?.token;

  const [productName, setProductName] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [price, setPrice] = useState(0);
  const [description, setDescription] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Fetch existing product info
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASEURL}/product/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        setProductName(data.productName);
        setQuantity(data.quantity);
        setPrice(data.price);
        setDescription(data.productDescription);
        setProductCategory(data.productCategory);
        setExistingImages(data.images || []);
      })
      .catch((err) => {
        console.error("Failed to load product", err);
        message.error("Could not load product details");
      });
  }, [productId, token]);

  const handleAcceptedFiles = (files) => {
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    if (!productName || quantity <= 0 || price <= 0 || !productCategory) {
      message.warning("Please fill all required fields");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("productName", productName);
      formData.append("quantity", quantity);
      formData.append("price", price);
      formData.append("productDescription", description);
      formData.append("productCategory", productCategory);
      formData.append("inStock", true);

      // Only include new images if uploaded
      selectedFiles.forEach((file) => {
        formData.append("images", file);
      });

      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/product/${productId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      message.success("Product updated successfully");
      navigate("/products");
    } catch (err) {
      console.error("Error updating product", err);
      message.error("Failed to update product");
    }
  };

  return (
    <div className="p-4">
      <Card title="Edit Product">
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>Product Name</Text>
            <Input value={productName} onChange={(e) => setProductName(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Quantity</Text>
            <InputNumber min={1} value={quantity} onChange={setQuantity} style={{ width: "100%" }} />
          </Col>
          <Col span={8}>
            <Text strong>Price</Text>
            <InputNumber
              min={1}
              value={price}
              onChange={setPrice}
              style={{ width: "100%" }}
              prefix="₹"
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={12}>
            <Text strong>Description</Text>
            <Input.TextArea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Col>
          <Col span={12}>
            <Text strong>Product Category</Text>
            <Select
              showSearch
              value={productCategory}
              onChange={setProductCategory}
              style={{ width: "100%" }}
              placeholder="Select Category"
            >
              <Option value="posm">POSM</Option>
              <Option value="asset">Assets</Option>
            </Select>
          </Col>
        </Row>

        <Row style={{ marginTop: 20 }}>
          <Col span={24}>
            <Text strong>Product Images</Text>
            <Dropzone onDrop={handleAcceptedFiles} accept={{ "image/*": [] }} multiple>
              {({ getRootProps, getInputProps }) => (
                <div
                  {...getRootProps()}
                  style={{
                    border: "2px dashed  #d9d9d9",
                    padding: "30px",
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: "4px",
                  }}
                >
                  <input {...getInputProps()} />
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/1828/1828491.png"
                    alt="Upload"
                    style={{ width: "40px", marginBottom: "10px", opacity: 0.5 }}
                  />
                  <p>Drag & drop new images here, or click to select</p>
                </div>
              )}
            </Dropzone>

            <Row gutter={[16, 16]} style={{ marginTop: 10 }}>
              {existingImages.map((url, idx) => (
                <Col span={4} key={idx}>
                  <Card
                    cover={
                      <img
                        src={`data:image/png;base64,${url}`}
                        alt={`Existing ${idx}`}
                        style={{ height: 100, objectFit: "cover" }}
                      />
                    }
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row justify="start" style={{ marginTop: 20 }}>
          <Button
            type="primary"
            onClick={handleSubmit}
            icon={<EditOutlined />}
            style={{ backgroundColor: PRIMARY_RED, borderColor: PRIMARY_RED, marginRight: 12 }}
          >
            Edit Product
          </Button>
          <Button type="link" style={{ color: PRIMARY_RED }} icon={<ArrowLeftOutlined />} onClick={() => navigate("/products")}>
            Back
          </Button>
        </Row>
      </Card>
    </div>
  );
};

export default EditProduct;
